import React, { useState, useEffect, useMemo } from 'react';
import { MedicationScheduleEntry, MyMedication } from './types';
import { myMedicationService } from './lib/myMedicationService';
import { exportService } from './lib/exportService';

interface MedicationCalendarProps {
  myMedications: MyMedication[];
  onScheduleGenerated?: () => void;
}

// Funkcja pomocnicza do formatowania daty lokalnie (bez przesunięcia strefy czasowej)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Funkcja pomocnicza do utworzenia daty lokalnie (bez przesunięcia strefy czasowej)
const createLocalDate = (year: number, month: number, day: number): Date => {
  return new Date(year, month, day);
};

export const MedicationCalendar: React.FC<MedicationCalendarProps> = ({
  myMedications,
  onScheduleGenerated
}) => {
  // Oblicz dzisiejszą datę jako string - użyj useMemo dla wydajności
  const todayStr = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const monthStr = String(month + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
  }, []);

  // Inicjalizuj z dzisiejszą datą, używając lokalnej strefy czasowej
  const now = new Date();
  const [currentDate, setCurrentDate] = useState(() => {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const [scheduleEntries, setScheduleEntries] = useState<MedicationScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const detailsRef = React.useRef<HTMLDivElement>(null);

  // Pobierz wpisy z kalendarza dla aktualnego miesiąca
  useEffect(() => {
    const loadEntries = async () => {
      try {
        setLoading(true);

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDay = createLocalDate(year, month, 1);
        const lastDay = createLocalDate(year, month + 1, 0);

        const startDate = formatDateLocal(firstDay);
        const endDate = formatDateLocal(lastDay);

        console.log('[MedicationCalendar] load month range:', { startDate, endDate });

        const entries = await myMedicationService.getScheduleEntries(startDate, endDate, 'calendar');

        console.log('[MedicationCalendar] loaded entries:', entries.length);

        setScheduleEntries(entries);

        // ✅ DEBUG: wrzuć do window, żeby dało się to znaleźć w konsoli
        (window as any).__scheduleEntries = entries;
        (window as any).__scheduleMonthRange = { startDate, endDate };
      } catch (error) {
        console.error('Błąd ładowania wpisów z kalendarza:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, [currentDate, myMedications]);

  // Generuj raport PDF z przyjętymi lekami dla danego miesiąca
  const generatePDFReport = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = createLocalDate(year, month, 1);
      const lastDay = createLocalDate(year, month + 1, 0);

      const startDate = formatDateLocal(firstDay);
      const endDate = formatDateLocal(lastDay);

      const entries = await myMedicationService.getScheduleEntries(startDate, endDate, 'calendar');

      const monthName = monthNames[month];
      exportService.exportMedicationsToPDF(entries, myMedications, monthName, year, 'raport_lekow');
    } catch (error) {
      console.error('Błąd generowania raportu PDF:', error);
      alert('Błąd generowania raportu PDF. Sprawdź konsolę.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMedicationTaken = async (entryId: string, currentlyTaken: boolean) => {
    try {
      await myMedicationService.markAsTaken(entryId, !currentlyTaken);

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = createLocalDate(year, month, 1);
      const lastDay = createLocalDate(year, month + 1, 0);
      const startDate = formatDateLocal(firstDay);
      const endDate = formatDateLocal(lastDay);

      const entries = await myMedicationService.getScheduleEntries(startDate, endDate, 'calendar');
      setScheduleEntries(entries);

      // ✅ DEBUG: aktualizacja
      (window as any).__scheduleEntries = entries;
    } catch (error) {
      console.error('Błąd aktualizacji wpisu:', error);
      alert('Błąd aktualizacji. Sprawdź konsolę.');
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = createLocalDate(year, month, 1);
    const lastDay = createLocalDate(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(createLocalDate(year, month, day));
    }

    return days;
  };

  const getEntriesForDate = (date: Date): MedicationScheduleEntry[] => {
    const dateStr = formatDateLocal(date);
    // tu porównujemy do stringa YYYY-MM-DD
    return scheduleEntries.filter(e => e.date === dateStr);
  };

  const getMedicationName = (entry: MedicationScheduleEntry): string => {
    const medication = myMedications.find(m => m.id === entry.myMedicationId);
    return medication?.name || 'Nieznany lek';
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    let newYear = year;
    let newMonth = month;

    if (direction === 'prev') {
      newMonth = month - 1;
      if (newMonth < 0) {
        newMonth = 11;
        newYear = year - 1;
      }
    } else {
      newMonth = month + 1;
      if (newMonth > 11) {
        newMonth = 0;
        newYear = year + 1;
      }
    }

    setCurrentDate(createLocalDate(newYear, newMonth, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    const now = new Date();
    const todayDate = createLocalDate(now.getFullYear(), now.getMonth(), now.getDate());
    setCurrentDate(todayDate);
    setSelectedDate(todayStr);
  };

  const days = getDaysInMonth();
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];
  const dayNames = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => navigateMonth('prev')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Poprzedni miesiąc
        </button>

        <h2 style={{ margin: 0 }}>
          {monthNames[month]} {year}
        </h2>

        <button
          onClick={() => navigateMonth('next')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Następny miesiąc →
        </button>
      </div>

      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
        <button
          onClick={generatePDFReport}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: loading ? '#ccc' : '#9C27B0',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {loading ? 'Generowanie...' : '📄 Pobierz raport PDF z przyjętymi lekami'}
        </button>

        <button
          onClick={goToToday}
          style={{
            padding: '10px 20px',
            backgroundColor: '#ff9800',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          📅 Dzisiaj
        </button>
      </div>

      {/* Kalendarz */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: '4px',
        backgroundColor: '#f5f5f5',
        padding: '10px',
        borderRadius: '10px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Nagłówki dni */}
        {dayNames.map(day => (
          <div
            key={day}
            style={{
              padding: '10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '12px',
              color: '#666',
              minWidth: '0',
              overflow: 'hidden'
            }}
          >
            {day}
          </div>
        ))}

        {/* Dni miesiąca */}
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} style={{ minHeight: '100px' }} />;
          }

          const dateStr = formatDateLocal(date);
          const entries = getEntriesForDate(date);

          const isToday = dateStr === todayStr;
          const isSelected = selectedDate === dateStr;

          return (
            <div
              key={dateStr}
              onClick={() => {
                const newSelectedDate = isSelected ? null : dateStr;
                setSelectedDate(newSelectedDate);
                if (newSelectedDate) {
                  setTimeout(() => {
                    detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }
              }}
              style={{
                minHeight: '100px',
                padding: '8px',
                backgroundColor: isToday ? '#fff3cd' : isSelected ? '#e3f2fd' : 'white',
                border: isToday ? '2px solid #ff9800' : isSelected ? '2px solid #2196F3' : '1px solid #ddd',
                borderRadius: '5px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: '0',
                overflow: 'hidden',
                wordWrap: 'break-word',
                boxSizing: 'border-box'
              }}
            >
              <div style={{
                fontWeight: isToday ? 'bold' : 'normal',
                marginBottom: '5px',
                fontSize: '14px'
              }}>
                {date.getDate()}
              </div>

              {entries.length > 0 && (
                <div style={{ fontSize: '9px' }}>
                  {entries.slice(0, 2).map(entry => {
                    const taken = entry.taken;
                    const medicationName = getMedicationName(entry);
                    const shortName = medicationName.length > 15
                      ? medicationName.substring(0, 12) + '...'
                      : medicationName;

                    return (
                      <div
                        key={entry.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMedicationTaken(entry.id, taken);
                        }}
                        style={{
                          marginBottom: '3px',
                          padding: '3px 5px',
                          backgroundColor: taken ? '#4CAF50' : '#ff9800',
                          color: 'white',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          textDecoration: taken ? 'line-through' : 'none',
                          opacity: taken ? 0.7 : 1,
                          fontWeight: '500',
                          lineHeight: '1.2'
                        }}
                        title={`${medicationName} - ${entry.time} (${entry.dosage} szt)`}
                      >
                        <div style={{ fontWeight: 'bold' }}>
                          {taken ? '✓' : '○'} {shortName}
                        </div>
                        <div style={{ fontSize: '8px', opacity: 0.9 }}>
                          {entry.time} ({entry.dosage}szt)
                        </div>
                      </div>
                    );
                  })}

                  {entries.length > 2 && (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedDate(dateStr);
                        setTimeout(() => {
                          detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      style={{
                        fontSize: '9px',
                        color: '#2196F3',
                        marginTop: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e3f2fd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      +{entries.length - 2} więcej (kliknij, aby zobaczyć)
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Szczegóły wybranego dnia */}
      {selectedDate && (
        <div
          ref={detailsRef}
          style={{
            marginTop: '20px',
            padding: '20px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px',
            border: '1px solid #ddd'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0 }}>
              Zaplanowane leki na {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pl-PL', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              style={{
                padding: '5px 10px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ✕ Zamknij
            </button>
          </div>

          {getEntriesForDate(new Date(selectedDate + 'T12:00:00')).length === 0 ? (
            <p style={{ color: '#666' }}>Brak zaplanowanych leków na ten dzień</p>
          ) : (
            <div>
              {getEntriesForDate(new Date(selectedDate + 'T12:00:00'))
                .sort((a, b) => a.time.localeCompare(b.time))
                .map(entry => {
                  const medication = myMedications.find(m => m.id === entry.myMedicationId);
                  const allEntriesForMedication = getEntriesForDate(new Date(selectedDate + 'T12:00:00'))
                    .filter(e => e.myMedicationId === entry.myMedicationId);

                  return (
                    <div
                      key={entry.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '15px',
                        marginBottom: '12px',
                        backgroundColor: entry.taken ? '#e8f5e9' : 'white',
                        border: `2px solid ${entry.taken ? '#4CAF50' : '#ff9800'}`,
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          fontSize: '16px',
                          color: entry.taken ? '#2e7d32' : '#333'
                        }}>
                          {medication?.name || 'Nieznany lek'}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                          🕐 Godzina: <strong>{entry.time}</strong>
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                          💊 Liczba sztuk: <strong>{entry.dosage}</strong>
                        </div>
                        {(medication?.takingInstructions || medication?.withMeal) && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            🍽️ {medication?.takingInstructions === 'with_meal' || medication?.withMeal ? 'W trakcie posiłku' :
                              medication?.takingInstructions === 'before_meal' ? 'Przed posiłkiem' :
                              medication?.takingInstructions === 'after_meal' ? 'Po posiłku' :
                              medication?.takingInstructions === 'empty_stomach' ? 'Na czczo' : 'W trakcie posiłku'}
                          </div>
                        )}
                        {allEntriesForMedication.length > 1 && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                            {allEntriesForMedication.length === 1
                              ? 'Jedyna dawka tego leku dzisiaj'
                              : `${allEntriesForMedication.length} dawki tego leku dzisiaj (${allEntriesForMedication.filter(e => e.taken).length} przyjęte)`
                            }
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => toggleMedicationTaken(entry.id, entry.taken)}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: entry.taken ? '#4CAF50' : '#ff9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          minWidth: '140px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                        }}
                      >
                        {entry.taken ? '✓ Przyjęty' : '○ Oznacz jako przyjęty'}
                      </button>
                    </div>
                  );
                })}

              {/* Podsumowanie dnia */}
              {getEntriesForDate(new Date(selectedDate + 'T12:00:00')).length > 0 && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '8px',
                  border: '1px solid #2196F3'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                    📊 Podsumowanie dnia:
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Zaplanowane: <strong>{getEntriesForDate(new Date(selectedDate + 'T12:00:00')).length}</strong> leków |
                    Przyjęte: <strong style={{ color: '#4CAF50' }}>
                      {getEntriesForDate(new Date(selectedDate + 'T12:00:00')).filter(e => e.taken).length}
                    </strong> |
                    Pozostało: <strong style={{ color: '#ff9800' }}>
                      {getEntriesForDate(new Date(selectedDate + 'T12:00:00')).filter(e => !e.taken).length}
                    </strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legenda */}
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '5px', fontSize: '12px' }}>
        <strong>Legenda:</strong>
        <div style={{ marginTop: '5px' }}>
          <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#ff9800', borderRadius: '3px', marginRight: '5px' }}></span>
          Nie przyjęty
        </div>
        <div style={{ marginTop: '5px' }}>
          <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#4CAF50', borderRadius: '3px', marginRight: '5px' }}></span>
          Przyjęty
        </div>
        <div style={{ marginTop: '5px' }}>
          <span style={{ display: 'inline-block', width: '20px', height: '20px', border: '2px solid #ff9800', borderRadius: '3px', marginRight: '5px' }}></span>
          Dzisiaj
        </div>
      </div>
    </div>
  );
};
